<template>
  <div>
    <b-container fluid>
      <b-card no-body>
        <b-overlay
          :show="isLoading"
          rounded="sm"
        >
          <div class="m-2">
            <b-form>
              <b-form-group
                label="Bộ lọc"
                class="border border-primary rounded p-50"
                label-class="pb-25"
              >
                <b-row>
                  <b-col cols="12" md="4">
                    <b-form-group
                      label="Hệ đào tạo:"
                      label-for="trainingSystemSelected"
                    >
                      <v-select
                        v-model="trainingSystemSelected"
                        :options="trainingSystemsOptions"
                        :reduce="(option) => option.value"
                        @input="onChangeTrainingSystem"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="4">
                    <b-form-group
                      label="Khóa học:"
                      label-for="courseSelected"
                    >
                      <v-select
                        v-model="courseSelected"
                        :options="courseOptions"
                        :reduce="(option) => option.value"
                        @input="onChangeCourse"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="4">
                    <b-form-group
                      label="Ngành học:"
                      label-for="majorSelected"
                    >
                      <v-select
                        v-model="majorSelected"
                        :options="majorOptions"
                        :reduce="(option) => option.value"
                        @input="onChangeMajor"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="12" md="4">
                    <b-form-group
                      label="Lớp học:"
                      label-for="classSelect"
                    >
                      <v-select
                        v-model="classSelect"
                        :options="classOptions"
                        :reduce="(option) => option.value"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="4">
                    <b-form-group
                      label="Năm:"
                      label-for="yearSelected"
                    >
                      <v-select
                        v-model="yearSelected"
                        :options="yearOptions"
                        :reduce="(option) => option.value"
                        @input="onChangeYear"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" md="4">
                    <b-form-group
                      label="Học kỳ:"
                      label-for="semesterSelected"
                    >
                      <v-select
                        v-model="semesterSelected"
                        :options="semesterOptions"
                        :reduce="(option) => option.value"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-form-group>
            </b-form>
            <b-row>
              <b-col cols="12">
                <div class="mr-auto">
                  <b-form-checkbox
                    v-model="firstExam"
                    :value="1"
                    :unchecked-value="0"
                  >
                    Tính theo điểm thi lần 1
                  </b-form-checkbox>
                </div>
                <div class="text-right">
                  <b-button
                    v-show="classSelect > 0 && yearSelected > 0"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    style="margin-right: 5px"
                    @click="onSelect"
                  >
                    <span class="text-nowrap text-right"><feather-icon icon="ListIcon" /> Danh sách</span>
                  </b-button>
                  <b-button
                    v-show="itemsTable.length > 0"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="success"
                    style="margin-right: 5px"
                    @click="exportBangDiemKetQuaHocTap"
                  >
                    <span class="text-nowrap text-right"><feather-icon icon="DownloadIcon" /> Xuất excel</span>
                  </b-button>
                </div>
              </b-col>
            </b-row>
            <b-row class="mt-2">
              <b-col cols="12">
                <div class="mb-2 d-flex flex-wrap align-items-center">
                  <div class="mr-3 stats-block">
                    <strong>Học lực:</strong>
                    <span
                      v-for="s in xlhlStats"
                      :key="`xlhl-${s.label}`"
                      :class="['badge', badgeClassXLHL(s.label), 'mr-1']"
                    >
                      {{ s.label }}: {{ s.percent.toFixed(1) }}% ({{ s.count }})
                    </span>
                  </div>
                  <div class="mr-3 stats-block">
                    <strong>Rèn luyện:</strong>
                    <span
                      v-for="s in xlrlStats"
                      :key="`xlrl-${s.label}`"
                      :class="['badge', badgeClassXLRL(s.label), 'mr-1']"
                    >
                      {{ s.label }}: {{ s.percent.toFixed(1) }}% ({{ s.count }})
                    </span>
                  </div>
                  <div class="ml-auto text-right legend">
                    <span v-if="retakeLegendVisible" class="legend-item ml-2"><span class="legend-color legend-retake"></span> Thi lại</span>
                  </div>
                </div>
                <div class="table-wrapper">
                  <b-table
                    ref="table"
                    :small="true"
                    :hover="true"
                    :items="itemsTable"
                    :fields="computedFields"
                    :tbody-tr-class="rowClass"
                    bordered
                    striped
                    sticky-header="600px"
                    class="avg-table"
                  >
                    <template #cell(stt)="data">
                      <span class="stt-cell">{{ data.index + 1 }}</span>
                    </template>
                    <template #cell(XLHL)="data">
                      <span :class="['grade-badge', gradeClassXLHL(data.value)]">{{ data.value || '—' }}</span>
                    </template>
                    <template #cell(XL_RL)="data">
                      <span :class="['grade-badge', gradeClassXLRL(data.value)]">{{ data.value || '—' }}</span>
                    </template>
                    <template #cell(TBC_HT)="data">
                      <span :class="['score-cell', scoreColorClass(data.value)]">{{ data.value }}</span>
                    </template>
                    <template #cell(TBC10_HT)="data">
                      <span :class="['score-cell', scoreColorClass(data.value)]">{{ data.value }}</span>
                    </template>
                  </b-table>
                </div>
              </b-col>
            </b-row>
          </div>
        </b-overlay>
      </b-card>
    </b-container>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BContainer, BFormCheckbox,
  BFormGroup,
  BOverlay,
  BRow,
  BTable,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapActions, mapGetters } from 'vuex'
import { showErrorToast, showWarningToast } from '@/utils/notification'
import '@core/scss/vue/libs/vue-good-table.scss'
import Ripple from 'vue-ripple-directive'

export default {
  name: 'AvgStudentClass',
  directives: {
    Ripple,
  },
  components: {
    BFormCheckbox,
    BTable,
    BButton,
    BFormGroup,
    BCard,
    BCol,
    BContainer,
    BOverlay,
    BRow,
    vSelect,
  },
  data() {
    return {
      trainingSystemSelected: undefined,
      courseSelected: undefined,
      majorSelected: undefined,
      classSelect: undefined,
      yearSelected: undefined,
      semesterSelected: undefined,
      firstExam: 0,
      isLoading: false,
      contentRendered: false,
      generatingPdf: false,
      pdfDownloaded: false,
      controlValue: {
        showLayout: false,
        floatLayout: true,
        enableDownload: true,
        previewModal: true,
        paginateElementsByHeight: 1100,
        manualPagination: false,
        filename: 'Danhsachphach',
        pdfQuality: 2,
        pdfFormat: 'a4',
        pdfOrientation: 'portrait',
        pdfContentWidth: '800px',
      },
      contentPdf: {
        heDaoTao: 'Đại học chính quy',
        lop: null,
        namHoc: null,
        hocKy: null,
        phongThi: null,
        monHoc: null,
        lanThi: 1,
      },
      fieldsTable: [
        {
          key: 'Mã sinh viên', label: 'MSSV', stickyColumn: true, thClass: 'col-msv', tdClass: 'td-msv',
        },
        { key: 'Họ', label: 'Họ', thClass: 'col-name' },
        { key: 'Tên', label: 'Tên', thClass: 'col-name' },
        {
          key: 'Ngày sinh', label: 'Ngày sinh', thClass: 'col-date', tdClass: 'td-center',
        },
        {
          key: 'TBC HT', label: 'TBC HT', thClass: 'col-score', tdClass: 'td-center',
        },
        {
          key: 'TBC10 HT', label: 'TBC10 HT', thClass: 'col-score', tdClass: 'td-center',
        },
        {
          key: 'XLHL', label: 'XLHL', thClass: 'col-grade', tdClass: 'td-center',
        },
        {
          key: 'Điểm RL', label: 'Điểm RL', thClass: 'col-score', tdClass: 'td-center',
        },
        {
          key: 'XL RL', label: 'XL RL', thClass: 'col-grade', tdClass: 'td-center',
        },
      ],
      itemsTable: [],
      // configuration for retake detection if backend adds a field
      retakeConfig: {
        enabled: false,
        field: 'retake',
        truthyValues: [1, true, 'Y', 'YES'],
      },
    }
  },
  computed: {
    computedFields() {
      return [
        {
          key: 'stt', label: 'STT', thClass: 'col-stt', tdClass: 'td-center td-stt',
        },
        ...this.fieldsTable,
      ]
    },
    ...mapGetters({
      dataLists: 'avgStudentClass/dataLists',
      allYears: 'avgStudentClass/allYears',
      allTrainingSystems: 'avgStudentClass/allTrainingSystems',
      listTrainingSystemCourses: 'avgStudentClass/listTrainingSystemCourses',
      listCourseMajors: 'avgStudentClass/listCourseMajors',
      listCourseMajorClasses: 'avgStudentClass/listCourseMajorClasses',
      listYearSemesters: 'avgStudentClass/listYearSemesters',
      listSubjectsOrder: 'avgStudentClass/listSubjectsOrder',
      dataExport: 'avgStudentClass/dataExport',
    }),
    htmlToPdfOptions() {
      return {
        margin: 0,
        filename: 'Danhsachphach.pdf',
        image: {
          type: 'jpeg',
          quality: 0.98,
        },
        enableLinks: true,
        html2canvas: {
          scale: this.controlValue.pdfQuality,
          useCORS: true,
        },
        jsPDF: {
          unit: 'in',
          format: this.controlValue.pdfFormat,
          orientation: this.controlValue.pdfOrientation,
        },
      }
    },

    trainingSystemsOptions() {
      return this.allTrainingSystems.map(item => ({
        value: item.id,
        label: item.name,
      }))
    },
    courseOptions() {
      return this.listTrainingSystemCourses.map(item => ({
        value: item.id,
        label: item.name,
      }))
    },
    majorOptions() {
      return this.listCourseMajors.map(item => ({
        value: item.id,
        label: item.name,
      }))
    },
    classOptions() {
      return this.listCourseMajorClasses.map(item => ({
        value: item.id,
        label: item.name,
      }))
    },
    yearOptions() {
      return this.allYears
    },
    semesterOptions() {
      return this.listYearSemesters.map(item => ({
        value: item.id,
        label: item.name,
      }))
    },

    totalStudents() {
      return this.itemsTable.length
    },
    xlhlStats() {
      // XLHL column is fixed label 'XLHL'
      const counts = {}
      for (const row of this.itemsTable) {
        const label = row['XLHL'] || 'Chưa xếp loại'
        counts[label] = (counts[label] || 0) + 1
      }
      const order = ['Xuất sắc', 'Giỏi', 'Khá', 'Trung bình', 'Yếu', 'Kém', 'Chưa xếp loại']
      return Object.keys(counts)
        .map(k => ({ label: k, count: counts[k], percent: this.totalStudents ? (counts[k] * 100) / this.totalStudents : 0 }))
        .sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label))
    },
    xlrlStats() {
      // XL RL column label is 'XL RL'
      const counts = {}
      for (const row of this.itemsTable) {
        const label = row['XL RL'] || 'Chưa xếp loại'
        counts[label] = (counts[label] || 0) + 1
      }
      const order = ['Xuất sắc', 'Tốt', 'Khá', 'Trung bình', 'Yếu', 'Kém', 'Chưa xếp loại']
      return Object.keys(counts)
        .map(k => ({ label: k, count: counts[k], percent: this.totalStudents ? (counts[k] * 100) / this.totalStudents : 0 }))
        .sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label))
    },
    retakeLegendVisible() {
      return this.retakeConfig.enabled
    }
  },
  async created() {
    this.isLoading = true
    try {
      await Promise.all([this.begin()])
    } catch (e) {
      showErrorToast(this, e)
    } finally {
      this.isLoading = false
    }
  },
  methods: {
    defaultFields() {
      return [
        {
          key: 'Mã sinh viên',
          label: 'MSSV',
          stickyColumn: true,
          thClass: 'col-msv',
          tdClass: 'td-msv',
        },
        {
          key: 'Họ',
          label: 'Họ',
          thClass: 'col-name',
        },
        {
          key: 'Tên',
          label: 'Tên',
          thClass: 'col-name',
        },
        {
          key: 'Ngày sinh',
          label: 'Ngày sinh',
          thClass: 'col-date',
          tdClass: 'td-center',
        },
        {
          key: 'TBC HT',
          label: 'TBC HT',
          thClass: 'col-score',
          tdClass: 'td-center',
        },
        {
          key: 'TBC10 HT',
          label: 'TBC10 HT',
          thClass: 'col-score',
          tdClass: 'td-center',
        },
        {
          key: 'XLHL',
          label: 'XLHL',
          thClass: 'col-grade',
          tdClass: 'td-center',
        },
        {
          key: 'Điểm RL',
          label: 'Điểm RL',
          thClass: 'col-score',
          tdClass: 'td-center',
        },
        {
          key: 'XL RL',
          label: 'XL RL',
          thClass: 'col-grade',
          tdClass: 'td-center',
        },
      ]
    },
    badgeClassXLHL(label) {
      const map = this.sharedBadgeMap()
      return map[label] || 'badge-secondary'
    },
    badgeClassXLRL(label) {
      const map = this.sharedBadgeMap()
      return map[label] || 'badge-secondary'
    },
    sharedBadgeMap() {
      // Unified colors for both sections
      return {
        'Xuất sắc': 'badge-success',
        Giỏi: 'badge-primary',
        Tốt: 'badge-primary',
        Khá: 'badge-info',
        'Trung bình': 'badge-warning',
        Yếu: 'badge-danger',
        Kém: 'badge-dark',
        'Chưa xếp loại': 'badge-secondary',
      }
    },
    gradeClassXLHL(value) {
      const map = {
        'Xuất sắc': 'grade-excellent',
        Giỏi: 'grade-good',
        Khá: 'grade-above-avg',
        'Trung bình': 'grade-avg',
        Yếu: 'grade-weak',
        Kém: 'grade-fail',
      }
      return map[value] || 'grade-none'
    },
    gradeClassXLRL(value) {
      const map = {
        'Xuất sắc': 'grade-excellent',
        Tốt: 'grade-good',
        Khá: 'grade-above-avg',
        'Trung bình': 'grade-avg',
        Yếu: 'grade-weak',
        Kém: 'grade-fail',
      }
      return map[value] || 'grade-none'
    },
    scoreColorClass(value) {
      const v = parseFloat(value)
      if (Number.isNaN(v)) return ''
      if (v >= 3.6) return 'score-excellent'
      if (v >= 3.2) return 'score-good'
      if (v >= 2.5) return 'score-above-avg'
      if (v >= 2.0) return 'score-avg'
      if (v >= 1.0) return 'score-weak'
      return 'score-fail'
    },
    ...mapActions({
      begin: 'avgStudentClass/begin',
      getData: 'avgStudentClass/getData',
      getListTrainingSystemCourses:
        'avgStudentClass/getListTrainingSystemCourses',
      getListCourseMajors: 'avgStudentClass/getListCourseMajors',
      getListCourseMajorClasses: 'avgStudentClass/getListCourseMajorClasses',
      getListSubjectsOrder: 'avgStudentClass/getListSubjectsOrder',
      getListYearSemesters: 'avgStudentClass/getListYearSemesters',
      resetData: 'avgStudentClass/resetData',
      getDataExport: 'avgStudentClass/downloadExport',
    }),

    onChangeTrainingSystem() {
      if (this.trainingSystemSelected == null) {
        this.reset()
        this.$store.commit('avgStudentClass/SET_LISTTRAININGSYSTEMCOURSES', {
          data: [],
        })
        this.$store.commit('avgStudentClass/SET_LISTCOURSEMAJORS', {
          data: [],
        })
        this.$store.commit('avgStudentClass/SET_LISTCOURSEMAJORCLASSES', {
          data: [],
        })
        this.courseSelected = null
        this.majorSelected = null
        this.classSelect = null
      } else {
        this.getListTrainingSystemCourses({
          training_system_id: this.trainingSystemSelected,
        })
      }
    },
    onChangeCourse() {
      if (this.courseSelected == null) {
        this.reset()
        this.$store.commit('avgStudentClass/SET_LISTCOURSEMAJORS', {
          data: [],
        })
        this.$store.commit('avgStudentClass/SET_LISTCOURSEMAJORCLASSES', {
          data: [],
        })
        this.majorSelected = null
        this.classSelect = null
      } else {
        this.getListCourseMajors({
          course_id: this.courseSelected,
        })
      }
    },
    onChangeMajor() {
      if (this.majorSelected == null) {
        this.reset()
        this.$store.commit('avgStudentClass/SET_LISTCOURSEMAJORCLASSES', {
          data: [],
        })
        this.classSelect = null
      } else {
        this.getListCourseMajorClasses({
          course_id: this.courseSelected,
          major_id: this.majorSelected,
        })
      }
    },
    onChangeYear() {
      if (this.yearSelected == null) {
        this.reset()
        this.$store.commit('avgStudentClass/SET_LISTYEARSEMESTERS', {
          data: [],
        })
        this.semesterSelected = undefined
      } else {
        this.getListYearSemesters({
          year_no: this.yearSelected,
        })
      }
    },
    async reset() {
      await this.$store.commit('avgStudentClass/SET_DATA', { data: [] })
      this.itemsTable = this.dataLists
      this.$refs.table.refresh()
      await this.$store.commit('avgStudentClass/SET_LISTSUBJECTSORDER', {
        data: null,
      })
      this.fieldsTable = this.defaultFields()
    },
    async onSelect() {
      this.isLoading = true
      try {
        const semesterId = this.semesterSelected == null ? 0 : this.semesterSelected
        await this.getListSubjectsOrder({
          class_id: this.classSelect,
          course_id: this.courseSelected,
          year_no: this.yearSelected,
          semester_id: semesterId,
        })
        this.fieldsTable = this.defaultFields()
        if (this.listSubjectsOrder !== null) {
          // eslint-disable-next-line prefer-spread
          this.fieldsTable.splice.apply(
            this.fieldsTable,
            [4, 0].concat(this.listSubjectsOrder.split(',')),
          )
          await this.getData({
            class_id: this.classSelect,
            course_id: this.courseSelected,
            year_no: this.yearSelected,
            semester_id: semesterId,
            student_id: -1,
            first_value: this.firstExam,
          })
          this.itemsTable = this.dataLists
        } else {
          await this.reset()
          showWarningToast(this, 'Không có môn học nào trong kỳ này!')
        }
      } catch (e) {
        showErrorToast(this, e)
      } finally {
        this.isLoading = false
      }
    },
    formatJson(filterVal, jsonData) {
      return jsonData.map(v => filterVal.map(j => v[j]))
    },
    // Highlight row for retake if configured
    rowClass(item, type) {
      if (!item || type !== 'row') return ''
      if (this.retakeConfig.enabled) {
        const v = item[this.retakeConfig.field]
        if (this.retakeConfig.truthyValues.includes(v)) return 'row-retake'
      }
      return ''
    },
    async exportBangDiemKetQuaHocTap() {
      const semesterId = this.semesterSelected == null ? 0 : this.semesterSelected
      await this.getDataExport({
        class_id: this.classSelect,
        course_id: this.courseSelected,
        year_no: this.yearSelected,
        semester_id: semesterId,
        student_id: -1,
        first_value: this.firstExam,
      })

      const a = document.createElement('a')
      document.body.appendChild(a)
      const blob = new Blob([this.dataExport.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      a.href = url
      a.download = 'bang_ket_qua_hoc_tap.xlsx'
      a.click()
      window.URL.revokeObjectURL(url)
    },
    exportExcel() {
      this.isLoading = true
      if (this.itemsTable.length > 0) {
        try {
          const tHeader = [
            'Mã sinh viên',
            'Họ và tên',
            'Tên',
            'Ngày sinh',
            'TBC HT',
            'XLHL',
            'Điểm RL',
            'XL RL',
          ]
          // eslint-disable-next-line prefer-spread
          tHeader.splice.apply(
            tHeader,
            [4, 0].concat(this.listSubjectsOrder.split(',')),
          )
          const dataJson = this.formatJson(tHeader, this.itemsTable)
          import('@/utils/Export2Excel').then(excel => {
            excel.export_json_to_excel({
              header: tHeader,
              data: dataJson,
              filename: 'Bảng_điểm_tổng_hợp_kỳ_theo_lớp_chuyên_ngành',
              autoWidth: true,
              bookType: 'xlsx',
            })
          })
        } catch (error) {
          showErrorToast(this, error)
        } finally {
          this.isLoading = false
        }
      } else {
        showWarningToast(this, 'Chưa thực hiện lấy danh sách phách')
        this.isLoading = false
      }
    },
  },
  beforeRouteUpdate(to, from, next) {
    this.reset()
    next()
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.td-low {
  background-color: rgba(255, 0, 0, 0.08);
}
.row-retake {
  background-color: rgba(0, 123, 255, 0.06);
}
.legend {
  .legend-item {
    display: inline-flex;
    align-items: center;
  }
  .legend-color {
    display: inline-block;
    width: 14px;
    height: 14px;
    margin-right: 6px;
    border-radius: 2px;
    border: 1px solid rgba(0,0,0,0.08);
  }
  .legend-retake { background-color: rgba(0, 123, 255, 0.18); }
}

/* ===== Table wrapper ===== */
.table-wrapper {
  overflow: auto;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

/* ===== avg-table global ===== */
.avg-table {
  font-size: 1rem;
  margin-bottom: 0 !important;

  /* Header styling */
  thead th {
    background-color: #3b82f6 !important;
    color: #fff !important;
    text-align: center;
    vertical-align: middle !important;
    white-space: nowrap;
    font-weight: 600;
    letter-spacing: 0.02em;
    border-color: #2563eb !important;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  /* Subject columns (dynamic) — lighter header */
  thead th:not(.col-stt):not(.col-msv):not(.col-name):not(.col-date):not(.col-score):not(.col-grade) {
    background-color: #0ea5e9 !important;
  }

  tbody td {
    vertical-align: middle !important;
    padding: 0.35rem 0.6rem !important;
  }

  /* Zebra rows — override bootstrap striped with softer tone */
  tbody tr:nth-child(odd) td {
    background-color: #f8faff;
  }
  tbody tr:nth-child(even) td {
    background-color: #ffffff;
  }
  tbody tr:hover td {
    background-color: #eff6ff !important;
  }
}

/* ===== Column helpers ===== */
.col-stt   { width: 46px !important; min-width: 46px !important; }
.col-msv   { min-width: 110px; }
.col-name  { min-width: 90px; }
.col-date  { min-width: 95px; }
.col-score { min-width: 70px; }
.col-grade { min-width: 95px; }

.td-center { text-align: center !important; }
.td-stt    { color: #6b7280; font-size: 0.75rem; }
.td-msv    { font-weight: 600; color: #1e40af; font-family: monospace; }

/* STT pill */
.stt-cell {
  display: inline-block;
  min-width: 22px;
  text-align: center;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 10px;
  padding: 0 6px;
  font-size: 0.72rem;
  font-weight: 600;
}

/* ===== Grade badges (inline cell) ===== */
.grade-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
}
.grade-excellent { background: #dcfce7; color: #15803d; border: 1px solid #86efac; }
.grade-good      { background: #dbeafe; color: #1d4ed8; border: 1px solid #93c5fd; }
.grade-above-avg { background: #e0f2fe; color: #0369a1; border: 1px solid #7dd3fc; }
.grade-avg       { background: #fef9c3; color: #854d0e; border: 1px solid #fde047; }
.grade-weak      { background: #ffedd5; color: #c2410c; border: 1px solid #fdba74; }
.grade-fail      { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
.grade-none      { background: #f3f4f6; color: #6b7280; border: 1px solid #d1d5db; }

/* ===== Score coloring ===== */
.score-cell {
  font-weight: 600;
  display: inline-block;
}
.score-excellent { color: #15803d; }
.score-good      { color: #1d4ed8; }
.score-above-avg { color: #0369a1; }
.score-avg       { color: #92400e; }
.score-weak      { color: #c2410c; }
.score-fail      { color: #991b1b; }

/* Dark mode support */
.dark-layout {
  .avg-table {
    thead th {
      background-color: #1e3a8a !important;
      border-color: #1e40af !important;
    }
    tbody tr:nth-child(odd) td  { background-color: #1e2535; }
    tbody tr:nth-child(even) td { background-color: #1a2030; }
    tbody tr:hover td { background-color: #1e3a8a26 !important; }
  }
  .stt-cell       { background: #1e3a8a; color: #93c5fd; }
  .td-msv         { color: #93c5fd; }
  .grade-excellent { background: #14532d; color: #86efac; border-color: #166534; }
  .grade-good      { background: #1e3a8a; color: #93c5fd; border-color: #1d4ed8; }
  .grade-above-avg { background: #0c4a6e; color: #7dd3fc; border-color: #0369a1; }
  .grade-avg       { background: #713f12; color: #fde047; border-color: #854d0e; }
  .grade-weak      { background: #7c2d12; color: #fdba74; border-color: #c2410c; }
  .grade-fail      { background: #7f1d1d; color: #fca5a5; border-color: #991b1b; }
  .grade-none      { background: #374151; color: #9ca3af; border-color: #4b5563; }
}
</style>
